import type { FC, ReactNode } from 'react'
import type { SiteContent } from '@/types'

import {
    createContext,
    useCallback,
    useContext,
    useMemo
} from 'react'

// Site-wide content (settings, copy, footer links) that every page loads in getStaticProps
// with getSiteContent() and passes as the `site` prop. _app.tsx puts it in this context.

const SiteContentContext = createContext<SiteContent | null>(null)

interface SiteContentProviderProps {
    value: SiteContent | null
    children: ReactNode
}

export const SiteContentProvider: FC<SiteContentProviderProps> = ({ value, children }): ReactNode => {
    return (
        <SiteContentContext.Provider value={value}>
            {children}
        </SiteContentContext.Provider>
    )
}

export const useSiteContent = (): SiteContent => {
    const content = useContext(SiteContentContext)

    if (!content) throw new Error('Site content is missing. Load it with getSiteContent() in the page getStaticProps and return it as the `site` prop.')

    return content
}

// Plain string of a site_texts row, for attributes (placeholder, aria-label) and toasts.
// Visible copy goes through <CmsText> instead, so it stays editable in the preview.
export const useText = (): ((key: string) => string) => {
    const { texts } = useSiteContent()

    return useCallback((key: string): string => texts[key]?.value ?? '', [texts])
}

// Keys of a numbered group of texts, in order: useTextKeys('about.paragraphs.') returns
// ['about.paragraphs.1', 'about.paragraphs.2', ...], so a row added in the CMS shows up on its own.
export const useTextKeys = (prefix: string): string[] => {
    const { texts } = useSiteContent()

    return useMemo(() => {
        const suffix = (key: string): number => Number(key.slice(prefix.length))

        return Object.keys(texts)
            .filter(key => key.startsWith(prefix))
            .sort((a, b) => (suffix(a) - suffix(b)) || a.localeCompare(b))
    }, [texts, prefix])
}
